import {
  TabsBarStorage,
  ChangePwdStorage,
  TokenStorage,
  ShopTokenStorage,
  WebFunnyStorage,
  ShopInfoStorage,
} from '@/utils/storage'
import * as UserApi from '@/api/user'
import { filterRouteByTag } from '../../utils/handleRoutes'
import { clearHqEventPageCache } from '@/views/HQEvent/pageCache'
import shortcutManager from '@/utils/shortcut'

const state = () => ({
  accessToken: TokenStorage.get(),
  belongId: null,
  employeeName: null,
  employeeNo: null,
  belongName: null,
  belongCode: null,
  belongType: null,
  permissions: [],
  createTime: null,

  // 门店信息
  shopId: null,
  shopCode: null,
  shopName: null,
  businessGroupCode: null,
  cargoWarehouseCode: null,
  currencyCode: null,

  shopToken: null,

  limitViewTotalRevenue: false, // 限制员工查看总营收
})
const getters = {
  accessToken: (state) => state.accessToken,
  belongId: (state) => state.belongId,
  permissions: (state) => state.permissions,
  employeeName: (state) => state.employeeName,
  employeeNo: (state) => state.employeeNo,
  belongName: (state) => state.belongName,
  belongCode: (state) => state.belongCode,
  belongType: (state) => state.belongType,
  createTime: (state) => state.createTime,
  shopId: (state) => state.shopId ?? ShopInfoStorage.get()?.shopId,
  shopCode: (state) => state.shopCode ?? ShopInfoStorage.get()?.shopCode,
  shopName: (state) => state.shopName ?? ShopInfoStorage.get()?.shopName,
  businessGroupCode: (state) => state.businessGroupCode ?? ShopInfoStorage.get()?.businessGroupCode,
  cargoWarehouseCode: (state) => state.cargoWarehouseCode ?? ShopInfoStorage.get()?.cargoWarehouseCode,
  currencyCode: (state) => state.currencyCode ?? ShopInfoStorage.get()?.currencyCode,
  shopToken: (state) => state.shopToken,
  limitViewTotalRevenue: (state) => state.limitViewTotalRevenue,
  /** 是否启用新收银（统一快捷键中心 + ScannerCore） */
  hasNewCashier: (state) => {
    const menus = state.permissions?.[0]?.children || []
    return menus.some((route) => route.path === '/newCashier')
  },
}
const mutations = {
  removeAccessToken(state) {
    state.accessToken = ''
    TokenStorage.clear()
  },
  removeShopToken(state) {
    state.shopToken = null
    ShopTokenStorage.clear()
  },
  setAccessToken(state, accessToken) {
    state.accessToken = accessToken
    TokenStorage.set(accessToken)
  },
  setPermissions(state, permissions) {
    state.permissions = permissions
  },
  setBelongId(state, belongId) {
    state.belongId = belongId
  },
  setEmployeeNo(state, employeeNo) {
    state.employeeNo = employeeNo
  },
  setEmployeeName(state, employeeName) {
    state.employeeName = employeeName
  },
  setBelongName(state, belongName) {
    state.belongName = belongName
  },
  setBelongCode(state, belongCode) {
    state.belongCode = belongCode
  },
  setBelongType(state, belongType) {
    state.belongType = belongType
  },
  setCreateTime(state, createTime) {
    state.createTime = createTime
  },
  setShopToken(state, shopToken) {
    state.shopToken = shopToken
    ShopTokenStorage.set(shopToken)
  },
  setLimitViewTotalRevenue(state, limitViewTotalRevenue) {
    state.limitViewTotalRevenue = limitViewTotalRevenue
  },
  setShopInfo(state, shopInfo) {
    state.shopId = shopInfo?.shopId
    state.shopCode = shopInfo?.shopCode
    state.shopName = shopInfo?.shopName
    state.businessGroupCode = shopInfo.businessGroupCode
    state.cargoWarehouseCode = shopInfo.cargoWarehouseCode
    state.currencyCode = shopInfo.currencyCode
    ShopInfoStorage.set(shopInfo)
  },
  removeShopInfo(state) {
    state.shopId = null
    state.shopCode = null
    state.shopName = null
    state.businessGroupCode = null
    state.cargoWarehouseCode = null
    state.currencyCode = null
    ShopInfoStorage.clear()
  },
  clearUserData(state) {
    state.belongId = ''
    state.belongCode = ''
    state.belongName = ''
    state.belongType = ''
    state.employeeName = ''
    state.employeeNo = ''
    state.permissions = []
    state.createTime = ''
  },
}
const actions = {
  removeAccessToken({ commit }) {
    commit('removeAccessToken')
    commit('clearUserData')
    TabsBarStorage.clear()
  },
  removeShopToken({ commit }) {
    commit('removeShopToken')
  },
  async login({ commit }, loginInfo) {
    const { data } = await UserApi.login({
      ...loginInfo,
      belongType: 'DH_POS_LOGIN_USERS',
    })
    if (data.token) {
      commit('setAccessToken', data.token)
      ChangePwdStorage.set(data.needUpdatePwd)
    }
  },
  setShopToken({ commit }, data) {
    commit('setShopToken', data)
  },
  async getUserInfo({ commit, dispatch, state }) {
    const { data } = await UserApi.getUserInfo(state.accessToken)

    // 过滤零售终端的菜单
    data.permissionsMenus[0].children = filterRouteByTag(data.permissionsMenus[0].children)

    // 如果有收银权限，则获取热销商品持久化到本地 DB。
    if (
      data.permissionsMenus[0].children.find(
        (item) => item.componentName === 'Cashier' || item.componentName === 'NewCashier'
      )
    ) {
      dispatch('localDB/initHotSellToDB', {}, { root: true })
    }

    // 写入 webfunny userId
    if (process.env.VUE_APP_WEB_MONITOR_ID) {
      WebFunnyStorage.set({
        userId: data.employeeId, // 身份标识，可以是：身份证号、手机号、userId
        userTag: data.belongId, // 用户标签，可以是：某公司、某地方、某品牌
        projectVersion: '1.1.23',
        env: 'pro',
        platform: 'web', // 平台，可以是：web、app、小程序
      })
    }
    commit('setBelongId', data.belongId)
    commit('setPermissions', data.permissionsMenus)
    commit('setEmployeeName', data.employeeName)
    commit('setEmployeeNo', data.employeeNo)
    commit('setBelongName', data.belongName)
    commit('setBelongCode', data.belongCode)
    commit('setBelongType', data.belongType)
    commit('setCreateTime', data.createTime)
    commit('setLimitViewTotalRevenue', data.limitViewTotalRevenue)

    // ScannerCore 改由 NewCashier/newPayPanel 页 activated 按路由启停，避免冲到旧收银
    const hasNewCashier = data.permissionsMenus[0].children.some((route) => route.path === '/newCashier')
    if (!hasNewCashier) {
      shortcutManager.scannerBridge.stop()
    }

    dispatch('sys/getZipConfig', {}, { root: true })
    dispatch('payment/FETCH_PAYMENT_TYPE_LIST', {}, { root: true })
    dispatch('retailShop/FETCH_RETAILSHOP', {}, { root: true })
    dispatch('cashierStaff/FETCH_CASHIER_STAFF', {}, { root: true })
    dispatch('notice/FETCH_NOTICE_LIST', {}, { root: true })

    const hasTakeaway = data.permissionsMenus[0].children.some((route) => route.path === '/takeaway')

    // 获取外卖接单配置
    await dispatch('takeawayPrintList/PRINT_SETTING', {}, { root: true })

    // 通过条件判断是否轮询待打印外卖订单列表：
    // 1. 菜单中是否有外卖模块
    if (hasTakeaway) {
      dispatch('takeawayPrintList/STOP', {}, { root: true })
      dispatch('takeawayPrintList/FETCH_PRINT_LIST', {}, { root: true })
    }
    dispatch('notice/FETCH_NOTICE_REMINDNUMS', {}, { root: true })
    dispatch('feedback/FETCH_FEEDBACK_UNREADCOUNT', {}, { root: true })
    dispatch('eventBanner/FETCH_EVENT_BANNER', {}, { root: true })
  },

  setShopInfo({ commit }, data) {
    commit('setShopInfo', data)
  },

  removeShopInfo({ commit }) {
    commit('removeShopInfo')
  },

  // 修改密码
  async changePassword({ commit }, changePwdInfo) {
    await UserApi.putChangePassWord(changePwdInfo)
    commit('removeAccessToken')
    commit('removeShopToken')
    commit('removeShopInfo')
    ChangePwdStorage.clear()
  },
  // 退出登录清空 accessToken 和路由
  async logout({ dispatch, commit }, activeLogout) {
    activeLogout && (await UserApi.logout())
    shortcutManager.scannerBridge.stop()
    dispatch('removeAccessToken')
    dispatch('removeShopToken')
    dispatch('removeShopInfo')
    dispatch('routes/clear', {}, { root: true })
    dispatch('tabsBar/clear', {}, { root: true })
    dispatch('takeawayPrintList/STOP', {}, { root: true })
    dispatch('eventBanner/CLEAR_EVENT_BANNER', {}, { root: true })
    commit('eventBanner/SET_BLACKLISTED', false, { root: true })
    clearHqEventPageCache()
  },
}
export default { state, getters, mutations, actions }
